import { Injectable } from '@angular/core';
import { addressString } from '@core/util/address';
import { clientLegalName, getSignatories } from '@core/util/client';
import { isSet } from '@core/util/value';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { Business, BusinessAddress } from '@models/businesses';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessState } from '@state/business';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';

@Injectable({
	providedIn: 'root',
})
export class TD_PRIVACY_AGREEMENT_Mapper extends BaseFormMapper<TD_PRIVACY_AGREEMENT> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: TD_PRIVACY_AGREEMENT,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): TD_PRIVACY_AGREEMENT {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as TD_PRIVACY_AGREEMENT);
		const filteredMatterClients = getSignatories(matterClients) ?? [];

		for (let i = 0; i < filteredMatterClients.length; i++) {
			const client = filteredMatterClients[i];

			if (!isSet(_formData?.[`client${i + 1}Date`])) {
				_formData[`client${i + 1}Date`] = format(new Date(), 'MMMM dd, yyyy');
			}

			if (
				(client?.name || (client as PersonMatterClient)?.firstName) &&
				!isSet(_formData?.[`client${i + 1}Name`])
			) {
				_formData[`client${i + 1}Name`] = clientLegalName(
					client,
					nameFormattingSettings,
					{ applyFormatting: false },
				);
			}

			if (
				client?.serviceAddress &&
				!isSet(_formData?.[`client${i + 1}Address1`])
			) {
				_formData[`client${i + 1}Address1`] = addressString(
					client.serviceAddress,
				);
			}

			if (client?.phone && !isSet(_formData?.[`client${i + 1}Phone`])) {
				_formData[`client${i + 1}Phone`] = client.phone;
			}
		}

		return _formData;
	}

	MapValues(): Partial<TD_PRIVACY_AGREEMENT> {
		return {};
	}
}

const Mappings: TD_PRIVACY_AGREEMENT = {
	client1Date: 'Client1Date',
	client1Name: 'Client1Name',
	client1Address1: 'Client1Address1',
	client1Address2: 'Client1Address2',
	client1Phone: 'Client1Phone',
	client2Date: 'Client2Date',
	client2Name: 'Client2Name',
	client2Phone: 'Client2Phone',
	client2Address1: 'Client2Address1',
	client2Address2: 'Client2Address2',
	client3Name: 'Client3Name',
	client3Date: 'Client3Date',
	client3Address1: 'Client3Address1',
	client3Address2: 'Client3Address2',
	client3Phone: 'Client3Phone',
	client4Date: 'Client4Date',
	client4Name: 'Client4Name',
	client4Address1: 'Client4Address1',
	client4Phone: 'Client4Phone',
	client4Address2: 'Client4Address2',
};

export interface TD_PRIVACY_AGREEMENT {
	client1Date: string;
	client1Name: string;
	client1Address1: string;
	client1Address2: string;
	client1Phone: string;
	client2Date: string;
	client2Name: string;
	client2Address1: string;
	client2Address2: string;
	client2Phone: string;
	client3Date: string;
	client3Name: string;
	client3Address1: string;
	client3Address2: string;
	client3Phone: string;
	client4Date: string;
	client4Name: string;
	client4Address1: string;
	client4Address2: string;
	client4Phone: string;
}
